import { useListPreferences, type PlainUserPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";
import { useHasArchivedEnvironments } from "@/queries/environment-queries";
import { useAutoDeleteValues } from "@/hooks/use-auto-delete-values";
import { useOrganization } from "@/queries/organization-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export const PRIVACY_POLICY_LAST_UPDATED_DATE = new Date("2025-10-28T00:00:00Z");

export enum NudgeKeys {
    "PRIVACY_POLICY_UPDATED" = "PRIVACY_POLICY_UPDATED",
    "ARCHIVE_FEATURE_INTRODUCED" = "ARCHIVE_FEATURE_INTRODUCED",
    "FROM_GITPOD_TO_ONA" = "FROM_GITPOD_TO_ONA",
    "AUTO_DELETE_POLICY" = "AUTO_DELETE_POLICY",
}

type Nudge =
    | { key: NudgeKeys.PRIVACY_POLICY_UPDATED }
    | { key: NudgeKeys.ARCHIVE_FEATURE_INTRODUCED }
    | { key: NudgeKeys.FROM_GITPOD_TO_ONA }
    | { key: NudgeKeys.AUTO_DELETE_POLICY };

/**
 * Computes the active nudge (if any).
 * This ensures we only ever render one nudge at a time.
 */
export function useNudges(): { active?: Nudge; isPending: boolean; error: Error | null } {
    const { data: user, isPending: isPendingUser, error: errorUser } = useAuthenticatedUser();
    const { data: organization, isPending: isPendingOrg, error: errorOrg } = useOrganization();
    const { preferences, isPending: isPendingPreferences, error: errorPreferences } = useListPreferences();
    const {
        hasArchivedEnvironments,
        isPending: isPendingEnvironments,
        error: errorEnvironments,
    } = useHasArchivedEnvironments();
    const { currentAutoDeletePolicyValue, previousAutoDeletePolicyValue } = useAutoDeleteValues();

    if (isPendingUser || isPendingOrg || isPendingPreferences || isPendingEnvironments) {
        return { isPending: true, error: null };
    }

    if (errorUser || errorOrg || errorPreferences || errorEnvironments) {
        return { isPending: false, error: errorUser || errorOrg || errorPreferences || errorEnvironments };
    }

    if (!user) {
        return { isPending: isPendingUser, error: errorUser };
    }

    if (!preferences) {
        return { isPending: isPendingPreferences, error: errorPreferences };
    }

    // Enterprise orgs don't get privacy policy nudges
    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    if (!isEnterprise && !hasAcceptedCurrentPrivacyPolicy({ user, preferences })) {
        return {
            active: { key: NudgeKeys.PRIVACY_POLICY_UPDATED },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    // Only show FromGitpodToOnaNudge if the organization is on FREE_ONA tier
    const isFreeOna = organization?.tier === OrganizationTier.FREE_ONA;
    const isFree = organization?.tier === OrganizationTier.FREE;
    if ((isFreeOna || isFree) && !hasDismissedNudge(NudgeKeys.FROM_GITPOD_TO_ONA, preferences)) {
        return {
            active: { key: NudgeKeys.FROM_GITPOD_TO_ONA },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    if (hasArchivedEnvironments && !hasDismissedNudge(NudgeKeys.ARCHIVE_FEATURE_INTRODUCED, preferences)) {
        return {
            active: { key: NudgeKeys.ARCHIVE_FEATURE_INTRODUCED },
            isPending: isPendingEnvironments,
            error: errorEnvironments,
        };
    }

    // Check for auto-delete policy changes (lowest priority)
    // Show nudge only if there was a previous value AND it differs from current value
    // For new users (no previous value), don't show the nudge
    if (previousAutoDeletePolicyValue !== undefined && previousAutoDeletePolicyValue !== currentAutoDeletePolicyValue) {
        return {
            active: { key: NudgeKeys.AUTO_DELETE_POLICY },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    return { isPending: isPendingPreferences, error: errorPreferences };
}

function hasDismissedNudge(key: string, preferences: PlainUserPreference[]): boolean {
    const savedPreference = preferences?.find((pref) => pref.key === key);
    if (!savedPreference?.value || savedPreference.value === "false") {
        return false;
    }

    return !!savedPreference?.value;
}

function hasAcceptedCurrentPrivacyPolicy(options: { user: PlainUser; preferences: PlainUserPreference[] }): boolean {
    const { user, preferences } = options;
    const preference = preferences?.find((pref) => pref.key === NudgeKeys.PRIVACY_POLICY_UPDATED.toString());

    // If the user was created after the last privacy policy update, we don't need to show the nudge
    if (user.createdAt?.seconds && user.createdAt?.seconds > PRIVACY_POLICY_LAST_UPDATED_DATE.getTime() / 1000) {
        return true;
    }

    // If the user has already accepted the current privacy policy, we don't need to show the nudge
    if (preference?.value) {
        const acceptedAt = new Date(preference?.value);
        if (acceptedAt > PRIVACY_POLICY_LAST_UPDATED_DATE) {
            return true;
        }
    }

    return false;
}
